import { useState, type FormEvent } from "react";
import { adminApi, API_BASE_URL, SESSION_KEY } from "../lib/api";

interface AuthGateProps {
  onAuthenticated: () => void;
}

export function AuthGate({ onAuthenticated }: AuthGateProps) {
  const [accessKey, setAccessKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const key = accessKey.trim();
    if (!key) {
      setError("Enter the private admin access key.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    try {
      await adminApi.dashboard(key);
      sessionStorage.setItem(SESSION_KEY, key);
      setAccessKey("");
      onAuthenticated();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "The access key could not be verified."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="access-page">
      <section className="access-card" aria-labelledby="access-title">
        <div className="access-card__brand" aria-label="ATALAY">
          ATALAY
        </div>
        <p className="eyebrow">Private administration</p>
        <h1 id="access-title">Enter the access key</h1>
        <p className="access-card__intro">
          This console controls published website content and business
          inquiries. The key is kept only for this browser session.
        </p>

        <form onSubmit={handleSubmit} className="stack" noValidate>
          <div className="field">
            <label htmlFor="admin-access-key">Admin access key</label>
            <div className="password-field">
              <input
                id="admin-access-key"
                type={showKey ? "text" : "password"}
                autoComplete="current-password"
                autoFocus
                value={accessKey}
                onChange={(event) => setAccessKey(event.target.value)}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "access-error" : "access-help"}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="button button--quiet password-field__toggle"
                onClick={() => setShowKey((current) => !current)}
                aria-pressed={showKey}
              >
                {showKey ? "Hide" : "Show"}
              </button>
            </div>
            <p className="field__help" id="access-help">
              The key is never written to local storage.
            </p>
            {error && (
              <p className="field__error" id="access-error" role="alert">
                {error}
              </p>
            )}
          </div>
          <button
            type="submit"
            className="button button--primary button--wide"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Verifying…" : "Open console"}
          </button>
        </form>

        <p className="access-card__endpoint">
          API <span>{API_BASE_URL}</span>
        </p>
      </section>
    </main>
  );
}
